import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export interface SiteSettings {
  site_name: string;
  site_description: string;
  contact_email: string;
  phone_number: string;
  address: string;
  working_hours: string;
}

const DEFAULT_SETTINGS: SiteSettings = {
  site_name: 'Big PineApple',
  site_description: 'Магазин ноутбуков',
  contact_email: 'info@pineapple-rnd.ru',
  phone_number: '8 (958) 039-34-50',
  address: 'Ростов-на-Дону, Буденновский проспект, д. 72/213',
  working_hours: 'Вт-Сб: 10:00 - 19:00, Вс,Пн: Выходной',
};

const LEGACY_WORKING_HOURS = 'Вт-Вс: 10:00 - 20:00, Пн: Выходной';
const CURRENT_WORKING_HOURS = 'Вт-Сб: 10:00 - 19:00, Вс,Пн: Выходной';

function normalizeWorkingHours(value: string | null | undefined): string {
  if (!value || value === LEGACY_WORKING_HOURS) {
    return CURRENT_WORKING_HOURS;
  }

  return value;
}

// Кэш настроек для избежания лишних запросов
let cachedSettings: SiteSettings | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 минут

export function useSiteSettings() {
  const [settings, setSettings] = useState<SiteSettings>(cachedSettings || DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(!cachedSettings);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const now = Date.now();
    
    // Используем кэш, если он свежий
    if (cachedSettings && (now - cacheTimestamp) < CACHE_DURATION) {
      setSettings(cachedSettings);
      setLoading(false);
      return;
    }

    async function fetchSettings() {
      try {
        const { data, error } = await supabase
          .from('site_settings')
          .select('key, value');

        if (error) throw error;

        if (data && data.length > 0) {
          const settingsObj: SiteSettings = { ...DEFAULT_SETTINGS };
          
          data.forEach(({ key, value }) => {
            if (key in settingsObj) {
              if (key === 'working_hours') {
                settingsObj.working_hours = normalizeWorkingHours(value);
                return;
              }

              (settingsObj as any)[key] = value || DEFAULT_SETTINGS[key as keyof SiteSettings];
            }
          });

          cachedSettings = settingsObj;
          cacheTimestamp = Date.now();
          setSettings(settingsObj);
        }
      } catch (err) {
        console.error('Error fetching site settings:', err);
        setError('Ошибка загрузки настроек');
        // Используем дефолтные значения при ошибке
        setSettings(DEFAULT_SETTINGS);
      } finally {
        setLoading(false);
      }
    }

    fetchSettings();
  }, []);

  return { settings, loading, error };
}

// Функция для обновления настроек (для админки)
export async function updateSiteSettings(updates: Partial<SiteSettings>): Promise<boolean> {
  try {
    const promises = Object.entries(updates).map(([key, value]) =>
      supabase
        .from('site_settings')
        .upsert({ key, value }, { onConflict: 'key' })
    );

    const results = await Promise.all(promises);
    
    const hasError = results.some(r => r.error);
    if (hasError) {
      console.error('Error updating settings:', results.filter(r => r.error));
      return false;
    }

    // Инвалидируем кэш
    cachedSettings = null;
    cacheTimestamp = 0;

    return true;
  } catch (err) {
    console.error('Error updating site settings:', err);
    return false;
  }
}

// Функция для получения настроек (для серверных операций)
export async function getSiteSettings(): Promise<SiteSettings> {
  try {
    const { data, error } = await supabase
      .from('site_settings')
      .select('key, value');

    if (error) throw error;

    const settingsObj: SiteSettings = { ...DEFAULT_SETTINGS };
    
    if (data) {
      data.forEach(({ key, value }) => {
        if (key in settingsObj) {
          if (key === 'working_hours') {
            settingsObj.working_hours = normalizeWorkingHours(value);
            return;
          }

          (settingsObj as any)[key] = value || DEFAULT_SETTINGS[key as keyof SiteSettings];
        }
      });
    }

    return settingsObj;
  } catch (err) {
    console.error('Error fetching site settings:', err);
    return DEFAULT_SETTINGS;
  }
}
